// Generated by Claude Code
import { useEffect } from 'react'
import CompanySettingsApi from '#core/api/auth/CompanySettingsApi.ts'
import type { AuthenticationProvider } from '#core/auth/authentication-provider.ts'
import { State, useCustomGlobalState } from '#core/state/State.ts'
import { useUserState } from '#core/state/UserState.ts'

/** The active company's SSO providers, loaded once per company; empty until loaded or when the load fails. */
export class SsoProvidersState extends State<SsoProvidersState> {
  providers: AuthenticationProvider[] = []
  _attemptedCompanyId: string | null = null

  get hasSsoProvider(): boolean {
    return this.providers.length > 0
  }

  /** Loads the providers unless this company was already attempted; a failure is not retried until {@link invalidate}. */
  async ensureLoaded(companyId: string): Promise<void> {
    if (this._attemptedCompanyId === companyId) return
    this._attemptedCompanyId = companyId
    this.reactTo.providers = []
    try {
      const settings = await new CompanySettingsApi().getSettings()
      if (this._attemptedCompanyId === companyId) this.reactTo.providers = settings?.sso_providers ?? []
    } catch {
      // Hides the SSO controls, which only matter once a provider is connected.
    }
  }

  invalidate(): void {
    this._attemptedCompanyId = null
    this.reactTo.providers = []
  }
}

/** The active company's connected SSO providers, shared by every SSO control instead of each fetching company settings. */
export function useSsoProviders(): SsoProvidersState {
  const userState = useUserState()
  const state = useCustomGlobalState(() => new SsoProvidersState(undefined, null), 'SsoProvidersState')
  useEffect(() => {
    if (userState.loginState === 'failed') state.invalidate()
    else if (userState.loginState === 'success' && userState.activeCompanyId) void state.ensureLoaded(userState.activeCompanyId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.loginState, userState.activeCompanyId])
  return state
}
